/**
 * useSSEStream — SSE connection hook with reconnection, Last-Event-ID replay,
 * exponential backoff, and connection state machine.
 *
 * UX Spec Section 7.4 / Story 1.3 AC#1.
 *
 * Typed events:
 *   table.profile_start, table.profile_complete, table.profile_error,
 *   session.complete, session.checkpoint
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { MIN_BACKOFF_MS, planReconnect } from "./sseBackoff";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SSEConnectionState =
  | "connected"
  | "slow"
  | "disconnected"
  | "polling"
  /** The hook gave up after repeated failed connects (see `sseBackoff.ts`);
   * the UI shows a Retry that calls `reconnect`. */
  | "stopped";

export interface TableProfileStartEvent {
  type: "table.profile_start";
  tableName: string;
  tierAssigned: string;
}

export interface TableProfileCompleteEvent {
  type: "table.profile_complete";
  tableName: string;
  score: number;
  severity: string;
  columnCount: number;
  rowCount: number;
  issues: string[];
}

export interface TableProfileErrorEvent {
  type: "table.profile_error";
  tableName: string;
  error: string;
  retryable: boolean;
  /** Set only on the server's closing frame when access to the session ended
   * ("access_revoked"); a run that failed carries no code. */
  code?: string;
}

export interface SessionCompleteEvent {
  type: "session.complete";
  aggregateScore: number;
  tablesProfiled: number;
  elapsed: number;
}

export interface SessionCheckpointEvent {
  type: "session.checkpoint";
  tableIndex: number;
  totalTables: number;
  checkpointId: string;
}

export type SSEEvent =
  | TableProfileStartEvent
  | TableProfileCompleteEvent
  | TableProfileErrorEvent
  | SessionCompleteEvent
  | SessionCheckpointEvent;

export interface UseSSEStreamOptions {
  /** Backend SSE endpoint URL */
  url: string;
  /** Whether to connect (set false to pause) */
  enabled?: boolean;
  /** Callback for each parsed event */
  onEvent?: (event: SSEEvent) => void;
  /** Callback when connection state changes */
  onConnectionChange?: (state: SSEConnectionState) => void;
  /** Slow-connection threshold in ms (default 5000) */
  slowThreshold?: number;
}

export interface UseSSEStreamReturn {
  connectionState: SSEConnectionState;
  lastEventId: string | null;
  reconnect: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EVENT_TYPES = [
  "table.profile_start",
  "table.profile_complete",
  "table.profile_error",
  "session.complete",
  "session.checkpoint",
] as const;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSSEStream({
  url,
  enabled = true,
  onEvent,
  onConnectionChange,
  slowThreshold = 5000,
}: UseSSEStreamOptions): UseSSEStreamReturn {
  const [connectionState, setConnectionState] =
    useState<SSEConnectionState>("disconnected");
  const lastEventIdRef = useRef<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const backoffRef = useRef(MIN_BACKOFF_MS);
  const failuresRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMessageTimeRef = useRef<number>(0);
  const slowCheckTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable callback refs
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onConnectionChangeRef = useRef(onConnectionChange);
  onConnectionChangeRef.current = onConnectionChange;

  const updateState = useCallback((state: SSEConnectionState) => {
    setConnectionState(state);
    onConnectionChangeRef.current?.(state);
  }, []);

  const connect = useCallback(() => {
    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Build URL with Last-Event-ID for replay
    const connectUrl = lastEventIdRef.current
      ? `${url}${url.includes("?") ? "&" : "?"}lastEventId=${encodeURIComponent(lastEventIdRef.current)}`
      : url;

    const es = new EventSource(connectUrl);
    eventSourceRef.current = es;

    es.onopen = () => {
      backoffRef.current = MIN_BACKOFF_MS;
      failuresRef.current = 0;
      lastMessageTimeRef.current = Date.now();
      updateState("connected");
    };

    // Listen for typed events
    for (const eventType of EVENT_TYPES) {
      es.addEventListener(eventType, (e: MessageEvent) => {
        lastMessageTimeRef.current = Date.now();
        if (e.lastEventId) {
          lastEventIdRef.current = e.lastEventId;
        }

        try {
          const data = JSON.parse(e.data);
          const sseEvent: SSEEvent = { type: eventType, ...data };
          onEventRef.current?.(sseEvent);
        } catch {
          // Malformed JSON — skip silently
        }
      });
    }

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      updateState("disconnected");

      const plan = planReconnect(failuresRef.current, backoffRef.current);
      failuresRef.current = plan.failures;
      backoffRef.current = plan.backoffMs;
      if (plan.delayMs === null) {
        // Give up: surface "stopped" so the UI can offer a visible Retry
        // (`reconnect`); the caller's /status polling carries on meanwhile.
        updateState("stopped");
        return;
      }

      // Schedule reconnect with exponential backoff
      reconnectTimerRef.current = setTimeout(() => {
        updateState("polling");
        connect();
      }, plan.delayMs);
    };
  }, [url, updateState]);

  // Slow-connection detection
  useEffect(() => {
    slowCheckTimerRef.current = setInterval(() => {
      if (
        connectionState === "connected" &&
        lastMessageTimeRef.current > 0 &&
        Date.now() - lastMessageTimeRef.current > slowThreshold
      ) {
        updateState("slow");
      }
    }, 2000);

    return () => {
      if (slowCheckTimerRef.current) clearInterval(slowCheckTimerRef.current);
    };
  }, [connectionState, slowThreshold, updateState]);

  // Connect / disconnect based on enabled flag
  useEffect(() => {
    // A new url or a fresh enable is a new session: failures counted against
    // a previous one must not carry over and cut this one short.
    failuresRef.current = 0;
    backoffRef.current = MIN_BACKOFF_MS;
    if (enabled) {
      connect();
    } else {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      updateState("disconnected");
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [enabled, connect, updateState]);

  const reconnect = useCallback(() => {
    backoffRef.current = MIN_BACKOFF_MS;
    failuresRef.current = 0;
    updateState("polling"); // visible feedback for a Retry click
    connect();
  }, [connect, updateState]);

  return {
    connectionState,
    lastEventId: lastEventIdRef.current,
    reconnect,
  };
}
